<template>
  <div class="grid-content" ref="leadsTableContainer">
    <b-table :data="leads ? leads.Data : []" hoverable sticky-header
          :key="'leadsTable' + tableKey"
          :total="leads ? leads.Total : 0"
          backend-sorting default-sort="CreateDate" :default-sort-direction="'desc'"
          class="leads-table"
          :backend-filtering="true"
          draggable-column
          ref="leadsTable"
          @columndragstart="columndragstart"
          @columndrop="columndrop"
          @columndragover="columndragover"
          @columndragleave="columndragleave"
          @filters-change="onFiltersChange"
          @sort="onPageSort">
      <b-table-column v-for="column in columns" :key="column.id" :field="column.name" :cell-class="column.class" :label="column.label"
            :visible="column.visible && !column.forbidden && column.id < 1000"
            :width="column.width"
            :sortable="column.sortable" :searchable="column.searchable"
            :header-class="'column-th-' + column.name">
          
        <template v-if="column.searchable" #searchable="props">
          <b-input v-model="props.filters[props.column.field]" @blur="onColumnFiltersBlur" v-on:keyup.native.enter="onColumnFiltersEnter" />
        </template>
        <template v-slot="props">
            <div v-if="column.name === 'Id'">
                {{ props.row[column.name] }}
            </div>
            <div v-else-if="column.name === 'Price'">
                <span v-if="showLeadInCalculation(props.row) && checkChannelMetadata(props.row.ChannelMetadataType)">
                    {{ $t("Leads.InCalculation") }}
                </span>
                <span v-else
                      :data-channel-id="props.row.ChannelID"
                      :data-keyword-id="props.row.GoogleKeywordId > 0 ? props.row.GoogleKeywordId : props.row.Keyword"
                      :data-ad-id="props.row.AdIdExternal"
                      :data-status-id="props.row.LeadStatusID"
                      class="price-placeholder">
                    {{ getFormattedValue(props.row.Price, column.name) }}
                </span>
            </div>
            <div v-else-if="column.name === 'Keyword'">
                <b-tooltip v-if="(props.row.Keyword === null || props.row.Keyword === '') && props.row.ChannelMetadataType === ChannelMetadataType.GoogleAdwords" position="is-top" multilined type="is-dark">
                    <span class="roi-help">
                        <icon-base width="16" height="16" view-box="0 0 16 16" icon-name="chart-pie"><icon-question-circle /></icon-base>
                    </span>
                    <template #content>
                        {{ $t("Leads.Grid.EmptyKeywordTooltip") }}
                    </template>
                </b-tooltip>
                <span v-else-if="props.row.Keyword !== null">
                    {{ props.row.Keyword }}
                </span>
            </div>
            <div v-else-if="column.name === 'CampaignTitle'">
                <b-tooltip v-if="props.row.CampaignTitle !== null && props.row.CampaignIDView" position="is-top" multilined type="is-dark">
                    <template #content>
                        {{ props.row.CampaignIDView }}
                    </template>
                    <span>
                        {{ props.row.CampaignTitle }}
                    </span>
                </b-tooltip>

                <span v-else-if="props.row.CampaignTitle !== null">
                    {{ props.row.CampaignTitle }}
                </span>

                <span v-else>
                    {{ $t("Leads.Grid.EmptyKeywordTooltip") }}
                </span>
            </div>


            <div v-else :style="getColumnStyle(column)">
                <span :dir="valueDirection(column)">{{ getFormattedValue(props.row[column.name], column.name) }}</span>
                <b-tooltip v-if="showCreditCardProblemTooltip(props.row.CreditCardProblem, column.name)" position="is-top" multilined type="is-dark">
                    <span class="roi-help">
                        <icon-base width="16" height="16" view-box="0 0 16 16" icon-name="chart-pie"><icon-question-circle /></icon-base>
                    </span>
                    <template #content>
                        {{ $t("Companies.CreditCardProblem") }}
                    </template>
                </b-tooltip>
            </div>
        </template>
      </b-table-column>

      <b-table-column cell-class="cell-actions" field="actions" :label="$t('Leads.Grid.Columns.Actions')" width="265" :sticky="true" v-slot="props"
            :visible="isActionsColumnVisible">
          <div class="actions-list">
              <b-tooltip v-if="showAdActionButton(props.row)" :label="$t('Leads.Grid.ActionsColumn.ShowAd')" position="is-top" type="is-dark">
                  <div :data-aid="props.row.AdIdExternal"
                       :data-agroupId="props.row.AdGroupIdExternal"
                       :data-channelid="props.row.ChannelId">
                      <a class='btn btn-outline-primary btn-sm show-ad' href='#'
                         @click="showAd($event, props.row)">
                          <icon-base width="20" height="18" view-box="0 0 576 512" icon-name="bullhorn"><icon-bullhorn /></icon-base>
                      </a>
                  </div>
              </b-tooltip>
              <b-tooltip v-if="showEditActionButton(props.row)" :label="$t('Leads.Grid.ActionsColumn.Edit')" position="is-top" type="is-dark">
                  <div class="action" @click="emitToggleLeadEdit($event, props.row, ToggleLeadEditMode.None)">
                      <icon-base width="18" height="18" view-box="0 0 18 18" icon-name="pencil"><icon-pencil /></icon-base>
                  </div>
              </b-tooltip>
              <b-tooltip v-if="!props.row.Deleted" :label="$t('Leads.Grid.ActionsColumn.Note')" position="is-top" type="is-dark">
                  <div class="action" @click="emitToggleLeadEdit($event, props.row, ToggleLeadEditMode.Notes)">
                      <icon-base width="22" height="22" view-box="0 0 104.69 122.88" icon-name="note"><icon-note /></icon-base>
                  </div>
              </b-tooltip>
              <b-tooltip v-if="!props.row.Deleted" :label="$t('Leads.Grid.ActionsColumn.History')" position="is-top" type="is-dark">
                  <div class="action" @click="emitToggleLeadHistory($event, props.row.Id)">
                      <icon-base width="26" height="18" view-box="0 0 18 18" icon-name="eye"><icon-eye /></icon-base>
                  </div>
              </b-tooltip>
              <b-tooltip v-if="showFormUrl" v-show="props.row.FormUrl" :label="$t('Leads.Grid.ActionsColumn.FormUrl')" position="is-top" type="is-dark">
                  <a :href="props.row.FormUrl" class="action" target="_blank">
                      <icon-base width="20" height="10" view-box="0 0 20 10" icon-name="link"><icon-link /></icon-base>
                  </a>
              </b-tooltip>
              <b-tooltip v-if="showUrlReferer" v-show="props.row.UrlReferrer" :label="$t('Leads.Grid.ActionsColumn.UrlReferrer')" position="is-top" type="is-dark">
                  <a :href="props.row.UrlReferrer" class="action" target="_blank">
                      <icon-base width="18" height="15" view-box="0 0 18 15" icon-name="refer"><icon-refer /></icon-base>
                  </a>
              </b-tooltip>
              <b-tooltip v-if="props.row.CallIdExternal && props.row.StatusCall" :label="$t('Leads.Grid.ActionsColumn.ListenCall')" position="is-top" type="is-dark">
                  <a href="#" class="action" @click="showPlayer($event, props.row)">
                      <icon-base width="18" height="18" view-box="0 0 18 18" icon-name="play-circle"><icon-play-circle /></icon-base>
                  </a>
                  
              </b-tooltip>
              <b-tooltip :label="$t('Leads.Grid.ActionsColumn.Delete')" position="is-top" type="is-dark" v-if="!props.row.Deleted && canDeleteLeads">
                  <a href="#" class="action action-delete" @click="deleteLead($event, props.row.Id)">
                      <icon-base width="18" height="18" view-box="0 0 18 18" icon-name="times-circle"><icon-times-circle /></icon-base>
                  </a>
              </b-tooltip>
          </div>
          <div v-if="leadSettings[props.row.Id]?.showPlayer" class="audio-player">
            <audio controls style="width: 300px;min-width: 300px;">
              <source :src="`/Phones/DownloadRecord?callID=${props.row.CallIdExternal}`" type="audio/mp3" />
            </audio>
          </div>
      </b-table-column>
    </b-table>
  </div>
</template>

<script lang="ts">
import { BComponent } from "buefy/types/components";
import moment from "moment";
import { LoaderComponent } from "vue-loading-overlay";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { ChannelType } from "@/models/campaigns/ChannelType";
import { Lead, LeadsPageViewModel, RefreshGridMode, ToggleLeadEditMode } from "@/models/leads/Lead";
import { DataSourceResult } from "@/models/DataSourceResult";
import { FilterCollection } from "@/models/FilterCollection";
import { GridColumn } from "@/models/GridColumn";
import { CompanyPermission, LeadPermissionType } from "@/models/leads/CompanyPermission";
import { LeadsPageKind } from "@/models/LeadsPageKind";
import { LeadStatus } from "@/models/leads/LeadStatus";
import { Result } from "@/models/Result";
import { TableSettings } from "@/models/TableSettings";
import { UserData } from "@/models/UserData";
import HelperService from "@/services/HelperService";
import LeadService from "@/services/LeadService";
import ApiService from "@/services/api/ApiService";
import AdPreview from "../Campaigns/AdPreview.vue";
import IconQuestionCircle from "../icons/IconQuestionCircle.vue";
import i18n from "../../i18n";
import IconBase from "../../components/IconBase.vue";
import IconBullhorn from "../../components/icons/IconBullhorn.vue";
import IconNote from "../../components/icons/IconNote.vue";
import IconPencil from "../../components/icons/IconPencil.vue";
import IconEye from "../../components/icons/IconEye.vue";
import IconLink from "../../components/icons/IconLink.vue";
import IconRefer from "../../components/icons/IconRefer.vue";
import IconPlayCircle from "../../components/icons/IconPlayCircle.vue";
import IconTimesCircle from "../../components/icons/IconTimesCircle.vue";

interface ILeadSettings {
  showPlayer?: boolean;
}

const namespace = "leadsModule";

@Component({
  components: {
    AdPreview,
    IconBase,
    IconBullhorn,
    IconNote,
    IconPencil,
    IconEye,
    IconLink,
    IconRefer,
    IconPlayCircle,
    IconQuestionCircle,
    IconTimesCircle,
  },
})
export default class LeadsGridContent extends Vue {
  @Prop({ required: true }) readonly pageKind!: LeadsPageKind;
  

  //#region Vuex getters and actions

  @Getter("columns", { namespace }) columns!: GridColumn[];
  @Getter("columnsFilters", { namespace }) columnsFilters!: FilterCollection;
  @Getter("leads", { namespace }) leads!: DataSourceResult;
  @Getter("leadsLoading", { namespace }) leadsLoading!: boolean;
  @Getter("leadsPageData", { namespace }) leadsPageData!: LeadsPageViewModel;
  @Getter("page", { namespace }) page!: number;
  @Getter("tableSettings", { namespace }) tableSettings!: TableSettings;
  @Getter("uniqueByCampaign", { namespace }) uniqueByCampaign!: boolean;
  @Getter("userData", { namespace: "mainModule" }) userData!: UserData;
  @Action("setNeedToRefreshLeadsGrid", { namespace }) setNeedToRefreshLeadsGrid!: (needToRefreshLeadsGrid: RefreshGridMode) => void;
  @Action("setColumns", { namespace }) setColumns!: (columns: GridColumn[]) => void;
  @Action("setColumnsFilters", { namespace }) setColumnsFilters!: (columnsFilters: FilterCollection) => void;
  @Action("setGraphsHiddenOnScroll", { namespace }) setGraphsHiddenOnScroll!: (graphsHiddenOnScroll: boolean) => void;
  @Action("setSelectedLeadId", { namespace }) setSelectedLeadId!: (clientId?: number) => void;
  @Action("setPage", { namespace }) setPage!: (page: number) => void;
  @Action("setSelectedLead", { namespace }) setSelectedLead!: (selectedLead?: Lead) => void;
  @Action("setSelectedClient", { namespace }) setSelectedClient!: (selectedClient?: Lead) => void;
  @Action("setSortDir", { namespace }) setSortDir!: (sortDir: string) => void;
  @Action("setSortField", { namespace }) setSortField!: (sortField: string) => void;

  //#endregion Vuex getters and actions

  
  //#region Properties

  isColumnFiltersUpdated = false;
  tableKey = 1;
  leadsTableContainer: Element | Window | null = null;
  leadsTableLoader?: LoaderComponent = undefined;

  leadSettings: { [id: number]: ILeadSettings } = {};

  ChannelMetadataType = ChannelType;
  ToggleLeadEditMode = ToggleLeadEditMode;

  //#endregion Properties


  //#region Watchers

  @Watch("columns", { immediate: true })
  onColumnsChange(newValue: GridColumn[], oldValue: GridColumn[]): void {
    if (newValue === undefined) {
      return;
    } else {
      this.tableKey++;
    }
  }

  @Watch("pageKind", { immediate: false })
  onPageKindChange(): void {
    (this.$refs.leadsTable as BComponent).filters = {};
  }

  @Watch("leadsLoading", { immediate: true })
  onLeadsLoadingChange(newValue: boolean, oldValue: boolean): void {
    if (newValue) {
      this.leadsTableLoader = this.$loading.show({
        container: this.$refs.leadsTableContainer
      });
    } else {
      this.leadsTableLoader?.hide();
      if (this.$el) {
        if (window.innerWidth > 768) {
          this.leadsTableContainer = this.$el.querySelector(".leads-table .table-container");
          if (this.leadsTableContainer) {
            if (this.page === 1) {
              (this.leadsTableContainer as Element).scrollTop = 0;
            }
            this.leadsTableContainer.addEventListener('scroll', this.onScrollTable);
          }
        } else {
          this.leadsTableContainer = window;
          if (this.page === 1) {
            window.scrollTo({ top: 0 });
          }
          this.leadsTableContainer.addEventListener('scroll', this.onScrollWindow);
        }
      }
    }
  }

  //#endregion Watchers


  //#region Getters

  get isActionsColumnVisible(): boolean {
    const column: GridColumn | undefined = this.columns.find((i: GridColumn) => i.name === "Actions");
    if (column) {
      return column.visible;
    } else {
      return false;
    }
  }

  get canDeleteLeads(): boolean {
    return this.leadsPageData && this.leadsPageData.CanDeleteLeads && this.pageKind === LeadsPageKind.Leads;
  }

  get showFormUrl(): boolean {
    return this.leadsPageData && !this.leadsPageData?.ActiveRestictions?.some((i: CompanyPermission) => i.Restriction === LeadPermissionType.Url);
  }

  get showKeywords(): boolean {
    return this.leadsPageData && !this.leadsPageData?.ActiveRestictions?.some((i: CompanyPermission) => i.Restriction === LeadPermissionType.LeadsScreenKeywords);
  }

  get showUrlReferer(): boolean {
    return this.leadsPageData && !this.leadsPageData?.ActiveRestictions?.some((i: CompanyPermission) => i.Restriction === LeadPermissionType.Referal);
  }

  //#endregion Getters


  //#region Lifecycle

  mounted(): void {
    this.$root.$on("setFilterCollection", (filterCollection: FilterCollection) => {
      if (this.$refs.leadsTable && (this.$refs.leadsTable as BComponent).filters && filterCollection.filters) {
        filterCollection.filters.forEach((filter: FilterCollection) => {
          if (filter.field) {
            this.$set((this.$refs.leadsTable as BComponent).filters, filter.field, filter.value?.replace(/'/g, ""));
          }
        });
      }
    });
  }

  //#endregion Lifecycle


  //#region Handlers

  deleteLead(e: Event, id: number) {
    e.preventDefault();
    HelperService.showConfirmDialog(
      undefined,
      i18n.t("AreYouSure").toString(),
      i18n.t("Yes").toString(),
      i18n.t("No").toString(),
      () => {
        let loader = this.$loading.show({
          container: this.$refs.leadsTableContainer
        });
        ApiService.deleteLead(id).then((result: Result) => {
          if (result.Success) {
            this.setNeedToRefreshLeadsGrid(RefreshGridMode.Full);
          } else {
            HelperService.showError(result.Message);
          }
        }).finally(() => {
          loader.hide();
        });
      }
    );
  }

  emitToggleLeadEdit(e: Event, lead: Lead, mode: ToggleLeadEditMode): void {
    e.preventDefault();
    if (this.pageKind === LeadsPageKind.Clients) {
      this.setSelectedClient(lead);
      this.$emit("toggle-client-edit", mode);
    } else if (this.pageKind === LeadsPageKind.Leads) {
      this.setSelectedLead(lead);
      this.$emit("toggle-lead-edit", mode);
    }
  }

  emitToggleLeadHistory(e: Event, id: number): void {
    e.preventDefault();
    this.setSelectedLeadId(id);
    this.$emit("toggle-lead-history");
  }

  onColumnFiltersBlur(e: Event) {
    if (this.isColumnFiltersUpdated) {
      this.isColumnFiltersUpdated = false;
      this.setNeedToRefreshLeadsGrid(RefreshGridMode.Full);
    }
  }

  onColumnFiltersEnter(e: Event) {
    (e.target as HTMLInputElement).blur();
  }

  onFiltersChange(filterObject: any) {
    const filterCollection: FilterCollection = {
      logic: "and",
      filters: [],
    };
    for (const key of Object.keys(filterObject)) {
      const val: any = filterObject[key];
      if (val) {
        filterCollection.filters?.push({ field: key, operator: "contains", value: `'${val}'` });
      }
    }
    this.setColumnsFilters(filterCollection);
    this.isColumnFiltersUpdated = true;
  }

  onPageChange(page: number) {
    this.setPage(page);
    this.setNeedToRefreshLeadsGrid(RefreshGridMode.GridOnly);
  }

  onPageSort(fieldName: string, sortDirection: string, e: PointerEvent) {
    this.setSortDir(sortDirection);
    if (fieldName === "CallSecondsStr") {
      fieldName = "CallSeconds";
    }
    this.setSortField(fieldName);
    this.setPage(1);
    this.setNeedToRefreshLeadsGrid(RefreshGridMode.GridOnly);
  }

  onScrollWindow(e: Event) {
    const target: Document = e.target as Document;
    if (this.leads.Total && target.documentElement.scrollTop >= document.documentElement.offsetHeight - window.innerHeight
          && this.page * LeadService.getPageSize() < this.leads.Total) {
      if (this.leadsTableContainer) {
        this.leadsTableContainer.removeEventListener('scroll', this.onScrollWindow);
      };
      this.onPageChange(this.page + 1);
    }
  }

  onScrollTable(e: Event) {
    const target: HTMLDivElement = e.target as HTMLDivElement;
    const EXTRA_PIXELS = 2; // for browser zoom
    const contentHeight: number = target.scrollHeight - target.clientHeight - EXTRA_PIXELS;
    if (this.leads.Total && target.scrollTop >= contentHeight && this.page * LeadService.getPageSize() < this.leads.Total) {
      if (this.leadsTableContainer) {
        this.leadsTableContainer.removeEventListener('scroll', this.onScrollTable);
      };
      this.onPageChange(this.page + 1);
    }
    this.setGraphsHiddenOnScroll(target.scrollTop > 10 ? true : false);
  }

  //#endregion Handlers


  //#region Private methods

  checkChannelMetadata(channelMetadataType: ChannelType) {
    return [
      ChannelType.FacebookAdLeadCampaign,
      ChannelType.FacebookAds,
      ChannelType.GoogleAdwords,
      ChannelType.GoogleLeads,
      ChannelType.InstagramAdLeadCampaign,
      ChannelType.InstagramAds
    ].indexOf(channelMetadataType) >= 0;
  }

  getColumnStyle(column: GridColumn): string {
    return HelperService.getColumnStyle(column);
  }

  /**
   * Направление содержимого ячейки. Телефон всегда LTR: все его символы - цифры, "+", скобки,
   * дефисы - для алгоритма bidi слабые или нейтральные, поэтому в ивритской локали побеждало
   * базовое направление абзаца и "+1 (272) 598-1092" отрисовывалось как "598-1092 (272) 1+"
   * (#4452). Атрибут стоит на элементе, который несёт значение, поэтому сам элемент по-прежнему
   * размещается RTL-потоком и выравнивание колонки не меняется. Для остальных колонок атрибут не
   * выводится вообще (Vue опускает undefined), то есть направление наследуется как раньше.
   */
  valueDirection(column: GridColumn): string | undefined {
    return column.name === "Phone" ? "ltr" : undefined;
  }

  getFormattedValue(value: number | string | Date, fieldName: string): string {
    if (!value) {
      return "";
    }
    if (fieldName === "CreateDate" || fieldName === "MeetingStartDate") {
      return moment(value as Date).format("DD/MM/yyyy HH:mm");
    } else if (fieldName === "Price") {
      return i18n.locale === "he-IL" ? `₪${Math.ceil(value as number)}` : `${Math.ceil(value as number)}₪`;
    } else {
      return value as string;
    }
  }

  showAd(e: Event, lead: Lead): void {
    e.preventDefault();
    this.$buefy.modal.open({
        parent: this,
        component: AdPreview,
        hasModalCard: true,
        trapFocus: true,
        props: {
          "adGroupId": lead.AdGroupIdExternal,
          "adId": lead.AdIdExternal,
          "channelId": lead.ChannelId
        }
    })
  }

  showAdActionButton(lead: Lead): boolean {
    return this.showKeywords && lead.AdIdExternal !== null && lead.AdIdExternal !== "0";
  }

  showEditActionButton(lead: Lead): boolean {
    return !lead.Deleted && (this.leadsPageData?.EditAllLeads || lead.UserId == this.userData.Id);
  }
  
  showLeadInCalculation(lead: Lead): boolean {
    return !lead.IsCalculatedLead && lead.ChannelId > 0 && (!this.uniqueByCampaign || lead.ClientStageId !== LeadStatus.Doubled)
          && lead.ClientStageId !== LeadStatus.RepeatedIn1Min && lead.ClientStageId !== LeadStatus.Ignored;
  }

  showCreditCardProblemTooltip(creditCardProblem: boolean, columnName: string): boolean {
    const columns: string[] = ["Email", "FullName", "Phone"];
    return creditCardProblem && columns.includes(columnName);
  }

  showPlayer(e: Event, lead: Lead): void {
    e.preventDefault();
    if (!this.leadSettings[lead.Id]) {
      this.leadSettings[lead.Id] = {
        showPlayer: false,
      };
    }
    this.leadSettings[lead.Id].showPlayer = !this.leadSettings[lead.Id].showPlayer;
    this.leadSettings = Object.assign({}, this.leadSettings);
  }

  //#endregion Private methods


  //#region Columns drag and drop

  draggingColumn: any = null;
  draggingColumnIndex: any = null;

  columndragstart(payload: any) {
    // clear selection of text if exists (it messes with drag and drop)
    const selectionFn: () => Selection | null = window.getSelection;
    if (selectionFn) {
      let selection: Selection | null = selectionFn();
      if (selection !== null) {
        if (selection.empty) {  // Chrome
          selection.empty();
        } else if (selection.removeAllRanges) {  // Firefox
          selection.removeAllRanges();
        }
      }
    } else if ((document as any).selection) {  // IE?
      (document as any).selection.empty();
    }

    this.draggingColumn = payload.column;
    this.draggingColumnIndex = payload.index;
    payload.event.dataTransfer.effectAllowed = 'copy';
  }

  columndragover(payload: any) {
    payload.event.dataTransfer.dropEffect = 'copy';
    payload.event.target.closest('th').classList.add('is-selected');
    payload.event.preventDefault();
  }

  columndragleave(payload: any) {
    payload.event.target.closest('th').classList.remove('is-selected');
    payload.event.preventDefault();
  }

  columndrop(payload: any) {
    payload.event.target.closest('th').classList.remove('is-selected');
    const droppedOnColumnIndex = payload.index;
    let newColumns: GridColumn[] = JSON.parse(JSON.stringify(this.columns));
    newColumns.splice(droppedOnColumnIndex, 0, newColumns.splice(this.draggingColumnIndex, 1)[0]);
    this.setColumns([]);
    setTimeout(() => {
      this.setColumns(newColumns);
    });
  }

  //#endregion Columns drag and drop
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.grid-content {
  .b-table {
    .table {
      thead {
        th.column-th-Id {
          min-width: 120px;
        }
      }
    }
  }

  .table thead th,
  .table tbody td {

    &.cell-full-name,
    &.cell-company,
    &.cell-channel,
    &.cell-actions {
      @media (max-width: 479px) {
        white-space: normal;
      }

      .audio-player {
        display: block;
        min-width: 300px;
        width: 300px;

        audio {
          display: block;
          min-width: 300px;
          width: 300px;
        }
      }
    }
  }
}
</style>
